import Backbone from 'backbone'
import _ from 'lodash'
import template from './editor-toolbar.hbs'
import $ from 'jquery'
import ColorPicker from 'scoreboard/components/color-picker'
import * as commandOptions from './command-options'
import selects from 'scoreboard/components/selects'
import deviceType from 'scoreboard/components/device-type'
import LinkForm from './link-form'
import Tooltip from 'scoreboard/components/tooltip'
import linkify from 'scoreboard/components/linkify'
import closestUntil from 'canvas/lib/support/closest-until'
import { isV6 } from 'scoreboard/components/version'
import isRtl from 'scoreboard/components/is-rtl'

var Toolbar = Backbone.View.extend({
	className: 'rich-text-toolbar',

	events: {
		'click button[data-command]': '_onButtonClick',
		'click button.link-command': '_onLinkButtonClick',
		'change select.font-size': '_onSizeChange'
	},

	/**
	 *
	 * @param {Object} opt
	 *
	 * @param {tinyMCE.Editor} opt.editor - the corresponding tinyMCE editor instance
	 *
	 * @param {Editor} opt.editorView - the corresponding Editor (a Backbone View)
	 *
	 * @param {Boolean} [opt.selectAllMode=false] - if true, indicates the editor
	 * is in "select all" mode; this is used by text widgets in "selected" mode,
	 * where any command done on the toolbar is applied to all the text in the widget
	 */
	initialize: function (opt = {}) {
		this.editor = opt.editor
		this.editorView = opt.editorView
		this.options = opt

		_.bindAll(this, '_updateState', '_recordEditorState')

		this.editor.on('NodeChange', this._updateState)
	},

	render: function () {
		this.$el.html(template({
			sizes: commandOptions.sizes,
			buttonType: isV6() ? 'button-boolean' : 'button-default'
		}))

		this.colorPicker = new ColorPicker({
			className: ColorPicker.prototype.className + ' small',
			colors: commandOptions.richTextColors()
		})

		this.$('.option.color').append(this.colorPicker.el)
		this.colorPicker.render().color('gray-darker')
		this.listenTo(this.colorPicker, 'select', this._onColorChange.bind(this))

		this._updateState()

		// Clicking on some toolbar elements blurs the editor; we need to record the cursor/selection state
		// so that it can be refocused before executing the command
		this.colorPicker.$el.on('click', this._recordEditorState)

		this.selectize(this.$('select'))

		selects.el(this.$('select.font-size')).on('click', function () {
			if (emptySelection(this.editor)) {
				// #13043 prevents problem where after changing font size and clicking into the area where the cursor was,
				// new text ends up inside an `data-mce-type="bookmark"` element, the contents of which are ignored
				// by tinymce when calling editor.getContents()
				// However, we still need _recordEditorState() when a block of text is selected, so only do it when
				// we have just a cursor (empty selection)
				return
			}

			this._recordEditorState.apply(this, arguments)
		}.bind(this))

		return this
	},

	_recordEditorState: function (e) {
		if (this.options.selectAllMode && deviceType.isMobileSafari()) {
			// #26824 getBookmark() brings up the keyboard on iOS, which we don't
			// want if we're in a text widget in "selected" mode. Fortunately, we
			// don't really need the bookmark; we can just re-select everything
			// again later when a color is chosen
			return
		}

		this.editor.selection.lastFocusBookmark = null
		this._bookmark = this.editor.selection.getBookmark()

		if (deviceType.isMobileSafari()) {
			// this prevents iOS from hiding the keyboard after the color picker is clicked
			e.preventDefault()
			e.stopPropagation()
		}
	},

	_onButtonClick: function (e) {
		this.handleEvent(() => {
			var cmd = $(e.target).data().command

			if (cmd == 'insertOrderedList' || cmd == 'insertUnorderedList') {
				// #12281 If using Edge and the cursor is in an empty block (like an empty paragraph), and you create a list,
				// it will create the list and put the cursor **before** the list. So in that scenario, create a dummy
				// span before creating the list, execute the list command, move the cursor to the dummy span, and delete it
				var addDummy = deviceType.edge && cursorInEmptyBLock(this.editor)

				if (addDummy) {
					var rangeContainer = this.editor.selection.getSel().getRangeAt(0).startContainer

					rangeContainer.insertBefore($('<span class="rich-text-dummy">a</span>')[0],
						rangeContainer.childNodes[0])
				}
			}

			this.editor.execCommand(cmd)

			// #12249 Chrome and Safari's native commands for creating lists uses the styling from the current line,
			// but instead of using the existing classes, it produces corresponding styles
			// Example: the class 'rich-text-color-dark-malibu' is turned into the style attribute 'color: #00A9C3'
			//
			// We want the classes, though, so after these commands are executed, we replace the styles with corresponding
			// rich-text classes
			if (cmd == 'insertOrderedList' || cmd == 'insertUnorderedList') {
				if (addDummy) {
					// Note that we have to look up .rich-text-dummy instead of keeping a reference to it before creating
					// the command, because the command to create a list will actually destroy the original node
					var dummy = $(this.editor.targetElm).find('.rich-text-dummy')
					this.editor.selection.setCursorLocation(dummy[0], 0)
					dummy.remove()
				}

				var focusNode = this.editor.selection.getSel().focusNode

				if (focusNode.nodeType === Node.TEXT_NODE) {
					focusNode = focusNode.parentNode
				}

				// handle color style
				var color = $(focusNode).attr('color')
				if (color) {
					if (focusNode.tagName.toLowerCase() === 'font') {
						var colorClass = commandOptions.hexToColorClass(color)
						$(focusNode)
							.removeAttr('color')
							.addClass(colorClass)
					} else {
						var colorClass = commandOptions.rgbToColorClass(color)
						$(focusNode)
							.css('color', '')
							.addClass(colorClass)
					}
				}
			}

			this._updateState()
		})
	},

	_onSizeChange: function (e) {
		this.handleEvent(() => {
			// For https://github.com/SpiderStrategies/Scoreboard/issues/12237
			// The rich select and rich editor battle with focus events, so...

			// Fire blur so the current active element loses focus
			document.activeElement.blur()
			// and clear the tinymce editor manager focused editor
			this.editor.editorManager.focusedEditor = null

			this._refocus()
			if (this.options.selectAllMode && deviceType.isMobileSafari()) {
				this.editorView.selectAll()
			}

			var selected = this.$('select.font-size').val()

			this.editor.formatter.apply('text-size', {size: selected})

			// Clear other size classes after applying new size class. See _onColorChange() for explanation
			clearSelectionClasses(this.editor, commandOptions.sizes.map(function (s) {
					return 'rich-text-size-' + s
				}),
				'rich-text-size-' + selected)

			this.editorView._triggerChange()
			this._updateState()
		})
	},

	_onColorChange: function (selected) {
		this.handleEvent(() => {
			// Refocus the editor (focus is lost when opening tooltip)
			this._refocus()

			if (this.options.selectAllMode && deviceType.isMobileSafari()) {
				this.editorView.selectAll()
			}

			this.editor.formatter.apply('text-color', {color: selected.replace('rich-text-color-', '')})

			// After applying the new color class, clear all other color classes from the selected nodes and their descendants.
			// Doing this **after** applying the new class, because sometimes that process turns text nodes into elements
			// and it can be difficult to work with just text nodes. See the second issue described here:
			// https://github.com/SpiderStrategies/Scoreboard/issues/12831#issuecomment-322577411
			clearSelectionClasses(this.editor, _.flatten(commandOptions.richTextColors()), selected)

			this.editorView._triggerChange()

			this._updateState()
		})
	},

	_onLinkButtonClick: function () {
		if (this.linkTooltip) {
			// if the tooltip is already open, destroy and close it
			this._removeLinkTooltip()

			// Refocus the editor (focus is lost when opening tooltip)
			this._refocus()
			return
		}

		this._recordEditorState.apply(this, arguments)

		let selectedLink = this._getSelectedLink()
		let form = new LinkForm({
			href: selectedLink ? $(selectedLink).attr('href') : null
		})

		this.linkTooltip = new Tooltip({
			content: form,
			attachTo: this.$('.link-command'),
			// Show the link tooltip above the editor tooltip on iOS so that the
			// keyboard doesn't cover it
			placement: deviceType.isMobileSafari() ? 'above' : 'below',
			arrow: 'center',
			extraClass: 'link-form'
		}).render()

		this.listenTo(form, 'done', link => {
			// Refocus the editor (focus is lost when opening tooltip)
			this._refocus()

			this.editor.execCommand('mceInsertLink', false, {
				href: linkify(link.href),
				// target and rel will be stripped out by the server and manually re-added
				// when displayed later, but we'll still add them here because after saving
				// the welcome message, the text from the editor is used for display
				target: '_blank',
				rel: 'noopener noreferrer'
			})

			if (emptySelection(this.editor)) {
				// insert a zero-width space so that the cursor is inside the newly inserted link
				this.editor.execCommand('mceInsertContent', false, '&#8203;')
			}

			this._removeLinkTooltip()
		})

		this.listenTo(form, 'unlink', link => {
			// Refocus the editor (focus is lost when opening tooltip)
			this._refocus()

			this.editor.execCommand('Unlink')

			this._removeLinkTooltip()
		})

		this.listenTo(form, 'remove', () => this._removeLinkTooltip())
		this.listenTo(this.linkTooltip, 'remove', () => delete this.linkTooltip)
	},

	// if a link is currently selected or the cursor is in a link ("a" anchor element), returns
	// the anchor element
	_getSelectedLink: function () {
		let rng = this.editor.selection.getRng()
		let selectedNodes = getSelectedNodes(rng.startContainer, rng.startOffset, rng.endContainer, rng.endOffset)

		return _(selectedNodes).map(node => $(node).closest('a')[0])
			.filter(node => !!node)
			.head()
	},

	_refocus: function () {
		if (this._bookmark) {
			this.editor.focus()
			this.editor.selection.moveToBookmark(this._bookmark)
		}
	},

	_updateState: function () {
		if (this.editor.destroyed) {
			return
		}

		buttons.forEach(function (cmd) {
			this._updateButton(cmd)
		}.bind(this))

		// Update other UI components
		this._updateJustification()
		this._updateColorPicker()
		this._updateSizeSelect()
		this._updateLinkButton()
	},

	_updateJustification: function() {
		// Unlike the other buttons (bold, italic, ordered list, et al), the alignment buttons will
		// always have a default if nothing was explicitly selected (justifyLeft)
		// Fixes #59762 - RTL alignment buttons are not aligned correctly
		let justifyDirection = isRtl() ? 'justifyRight' : 'justifyLeft'

		// If no alignment button is selected, select the correct one based on RTL/LTR
		if (this.$('.alignment button.is-selected').length === 0) {
			this.$(`[data-command=${justifyDirection}]`).addClass('is-selected')
		}
	},

	_updateButton: function (cmd) {
		this.$('[data-command=' + cmd + ']').toggleClass('is-selected', !!this.editor.queryCommandState(cmd))
	},

	_updateColorPicker: function () {
		let colorClasses = getSelectedStyleClasses(this.editor, 'color', colorStyleToColorClass)
		let color
		if (colorClasses.length === 1) {
			color = colorClasses[0]
		} else {
			// if the selection has more than one font color, leave the picker blank
			color = ''
		}

		this.colorPicker.color(color)
	},

	_updateSizeSelect: function () {
		var sizeSelect = this.$('select.font-size')

		let sizeClasses = getSelectedStyleClasses(this.editor, 'font-size', sizeStyleToSizeClass)
		let size
		if (sizeClasses.length === 1) {
			size = sizeClasses[0].replace('rich-text-size-', '')
			// #53178 disable the empty option; it's only there so it can be
			// initially selected when text of multiple sizes is highlighted.
			// We don't want a user actually picking it.
			sizeSelect.find('option[value=""]').prop('disabled', true)
		} else {
			// if the selection has more than one font size, leave the select blank
			size = ''
		}

		// Update the select in case we changed the options
		this.selectize(sizeSelect)

		if (sizeSelect.val() !== size) {
			sizeSelect.val(size)
			selects.update(sizeSelect)
		}
	},

	selectize (selectElements) {
		// #26824 don't use rich select for mobile Safari, because it will open
		// up the keyboard unnecessarily. Native select gives us a spinner instead
		if (!deviceType.isMobileSafari()) {
			selects(selectElements, {
				disableKeyboardControl: true
			})
		}
	},

	_updateLinkButton: function () {
		this.$('button.link-command').toggleClass('is-selected', !!this._getSelectedLink())
	},

	/**
	 * Executes the given callback for a DOM event at an appropriate time. For
	 * most devices, it is called immediately, but iOS needs to defer to nextTick
	 */
	handleEvent: function (handler) {
		if (this.options.selectAllMode && deviceType.isMobileSafari()) {
			// #26824 when we're in "selected" mode for text widgets, we don't
			// want actions on the toolbar to open up the keyboard. The easiest
			// way to prevent this is to perform the action after the DOM event
			// is done, because iOS Safari won't focus except in a native event
			// handler (usually this is annoying, but it comes in handy here)
			process.nextTick(handler)
		} else {
			handler()
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeLinkTooltip()
		this.colorPicker && this.colorPicker.remove()
	},

	_removeLinkTooltip: function () {
		this.linkTooltip && this.linkTooltip.remove()
		delete this.linkTooltip
	}
})

function applyFormat (editor, possibleValues, selected) {
	var remove = []
	possibleValues.forEach(function (value) {
		if (value !== selected && editor.formatter.match(value)) {
			remove.push(value)
		}
	}.bind(this))

	editor.formatter.apply(selected)

	// Must remove old classes **after** adding the new class, or the internal tinymce range selector
	// gets confused and throws an error
	remove.forEach(function (value) {
		editor.formatter.remove(value)
	})
}

var buttons = [
	'bold', 'italic', 'underline',
	'justifyLeft', 'justifyCenter', 'justifyRight',
	'insertUnorderedList', 'insertOrderedList' ]

function clearSelectionClasses (editor, classes, keep) {
	function doClear (n) {
		// The "keep" classes may have been applied to the selected node (or nodes),
		// one or more of its children, or one of its ancestors.

		// There's no reason for a given node to have more that one of the set of classes
		// applied to it (ie class="rich-text-size-10 rich-text-size-14" is invalid), so
		// we can just find the closest ancestor (current node included) that has the
		// selected "keep" class, and make sure any duplicate classes are cleared out
		$(n).closest(`[class*=${keep}]`).each(function (clearMe) {
			clearClasses(this, classes, keep)
		})

		// Also clear out any duplicate classes from the descendants
		if (n.nodeType == Node.ELEMENT_NODE) {
			$(n).find('*').each(function () {
				clearClasses(this, classes, keep)
			})
			clearClasses(n, classes, keep)
		} else if (n.nodeType === Node.TEXT_NODE) {
			clearClasses(n.parentNode, classes, keep)
		}
	}

	var rng = editor.selection.getRng()
	var selectedNodes = getSelectedNodes(rng.startContainer, rng.startOffset,
		rng.endContainer, rng.endOffset)

	if (selectedNodes.length === 1 && selectedNodes[0].nodeType == Node.TEXT_NODE) {
		// if a single text node is selected, clear its parent element's classes
		var elementToClear = selectedNodes[0].parentNode
		// #29266 the class may not have been applied to the direct parent,
		// but a higher-level ancestor. So walk up the tree until we find the
		// node that had the class applied
		elementToClear = closestUntil(elementToClear, '.' + keep, editor.getElement())[0]

		if (!_.isNil($(elementToClear).data().mceBogus)) {
			// if that parent element is one of the tinymce "bogus" nodes, we want to find a non-bogus ancestor to clear
			// see first issue here: https://github.com/SpiderStrategies/Scoreboard/issues/12831#issuecomment-322577411
			var nonBogusAncestor = $(selectedNodes[0]).closest(':not([data-mce-bogus])')

			// but only clear that non-bogus ancestor if it only has bogus children
			if (nonBogusAncestor.find('[data-mce-bogus]').length === nonBogusAncestor.children().length) {
				elementToClear = nonBogusAncestor[0]
			}
		}

		doClear(elementToClear, classes, keep)
	} else {
		selectedNodes.forEach(function (n) {
			doClear(n, classes, keep)
		})
	}
}

// returns the closest element that is an ancestor of both elements
function commonAncestor (el1, el2) {
	return $(el1).parents().has(el2).first() [0]
}

// Clears all rich-text-* classes from the given node (but keep the one the user selected)
function clearClasses (node, classes, keep) {
	if (node.nodeType == Node.ELEMENT_NODE) {
		_(classes)
			.filter(function (clazz) { return clazz != keep })
			.forEach(function (clazz) {
				$(node).removeClass(clazz)
			})
	}

	return $(node)
}

// Returns true if the editor's current selection is "empty" (just a cursor, no text selected)
function emptySelection (editor) {
	var selection = editor.selection.getSel()

	if (selection.rangeCount > 1) {
		return false
	}

	var range = selection.getRangeAt(0)
	if (range.startContainer !== range.endContainer) {
		return false
	}

	return range.startOffset === range.endOffset
}

// Returns true if the editor's current selection is just a cursor in a block with no text
function cursorInEmptyBLock (editor) {
	if (!emptySelection(editor)) {
		return false
	}

	var range = editor.selection.getSel().getRangeAt(0)
	var block = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentNode : range.startContainer

	// Note: can't use block.innerHTML because there could be non-visible content, like <br
	return block.textContent.length === 0
}

// Gets all currently selected nodes
// Adapted from https://stackoverflow.com/a/7784176/1138751
function getSelectedNodes (startContainer, startOffset, endContainer, endOffset) {
	var node = startContainer
	var endNode = endContainer

	if (endNode.nodeType === Node.ELEMENT_NODE && endContainer.childNodes[endOffset-1] === startContainer) {
		// If the endContainer contains the startContainer, and the startContainer matches the end offset,
		// then really the end node is the same as the start node
		endNode = startContainer
	}

	// Special cases for a range that is contained within a single node
	if (node == endNode && node.nodeType === Node.TEXT_NODE) {
		return [ node ]
	} else if (node == endNode && node.nodeType === Node.ELEMENT_NODE && startOffset === endOffset) {
		if (node.childNodes.length === 0) {
			return [ node ]
		} else {
			return [node.childNodes[startOffset]]
		}
	}

	// Iterate nodes until we hit the end container
	var rangeNodes = []
	// if we run into the end of the selection, make sure we stop grabbing more nodes.
	// This wasn't happening when selecting text, changing the color, then changing the color again
	var forceEnd = endContainer.childNodes[endOffset-1]
	while (node && node != endNode) {
		var next = nextNode(node, forceEnd)

		if (next) {
			rangeNodes.push(node = next)
		} else {
			break
		}
	}

	// Add partially selected nodes at the start of the range
	if (startContainer.nodeType === Node.TEXT_NODE) {
		rangeNodes.unshift(startContainer)
	}

	return rangeNodes.length === 0 ? [startContainer] : rangeNodes
}

// Returns the next node that should be added to the range array
// Note that this differs from $.next() because it may be the
// node's firstChild, its parent, or its sibling
function nextNode (node, end) {
	if (node.hasChildNodes()) {
		return node.firstChild
	} else {
		function atEnd () {
			// #24807 if we're already at the end node, don't check for its
			// siblings, or it might walk the whole way up the DOM tree.
			// The hasClass('mce-content-body') check below would normally
			// catch that, but when in "select all" mode, there is no
			// mce-content-body at the root of the editor
			return node === end
		}
		if (atEnd()) {
			return null
		}

		while (node && !node.nextSibling) {
			node = node.parentNode
			if (atEnd()) {
				return null
			}
		}
		if (!node || $(node).hasClass('mce-content-body')) {
			return null
		}
		return atEnd() ? null : node.nextSibling
	}
}

let sizeStyleToSizeClassMap, colorStyleToColorClassMap

/**
 * Maps 'font-size' style values (eg "32px") to their corresponding
 * rich-text-size class (eg "rich-text-size-24")
 */
function sizeStyleToSizeClass (widgetEl, px) {
	if (!sizeStyleToSizeClassMap) {
		sizeStyleToSizeClassMap = styleToClassMap(widgetEl,
			'font-size', 'rich-text-size-', commandOptions.sizes)
	}
	return sizeStyleToSizeClassMap[px]
}

/**
 * Map of 'color' style values (eg "rgb(54, 84, 212)") to their corresponding
 * rich-text-color class (eg "rich-text-color-dark-slate-blue")
 */
function colorStyleToColorClass (widgetEl, hex) {
	if (!colorStyleToColorClassMap) {
		colorStyleToColorClassMap = styleToClassMap(widgetEl,
			'color', '', _.flatten(commandOptions.richTextColors()))
	}
	return colorStyleToColorClassMap[hex]
}

function styleToClassMap (widgetEl, style, classPrefix, options) {
	let testContainer = $('<div style="visibility: hidden; position: absolute; top: 0px;">')
		.appendTo(widgetEl)

	let map = _(options).map(s => {
		let clazz = classPrefix + s
		let px = testContainer.addClass(clazz).css(style)
		testContainer.removeClass(clazz)
		return [px, classPrefix + s]
	}).fromPairs().value()

	testContainer.remove()

	return map
}

/**
 * Returns all matching style values used in the node or its descendants.
 *
 * Example: stylesInNode(node, 'font-size') returns something like ['24px', '32px']
 */
function stylesInNode (node, style) {
	if (!node) {
		return []
	}

	if (node.nodeType === Node.TEXT_NODE) {
		return [$(node.parentNode).css(style)]
	}

	let childStyles = _.flatMap(Array.from(node.children), child => {
		return stylesInNode(child, style)
	})

	return childStyles.concat([$(node).css(style)])
}

/**
 * Returns all style classes used in the editor's current selection that affect
 * the given style.
 *
 * Example: getSelectedStyleClasses(editor, 'font-size', sizeStyleToSizeClass)
 * returns something like ['rich-text-size-16', 'rich-text-size-26']
 */
function getSelectedStyleClasses (editor, style, styleToClassMapFunction) {
	let rng = editor.selection.getRng()
	let selectedNodes = getSelectedNodes(rng.startContainer, rng.startOffset, rng.endContainer, rng.endOffset)

	let editorNode = editor.getBody()

	let selectedNodesInEditor = selectedNodes.filter(node => {
		if (!node) {
			return false
		}

		let element = node.nodeType === Node.TEXT_NODE ? node.parentNode : node
		return editorNode.contains(element) || editorNode === element
	})

	let allStyles = _.flatMap(selectedNodesInEditor, n => stylesInNode(n, style))

	return _(allStyles).uniq()
		.map(style => {
			return styleToClassMapFunction(editorNode, style)
		})
		.filter(clazz => !_.isNil(clazz))
		.value()
}

_.extend(Toolbar, {
	clearSelectionClasses
})
export default Toolbar
export { clearSelectionClasses }

Toolbar.__filename__ = 'components/rich-text/editor-toolbar.js'
